import React, { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import ModalWrapper from "../../common/ModalWrapper";
import ToggleSwitch from "./ToggleSwitch";

const JobPostFormModal = ({
  mode = "create",
  API,
  open,
  onClose,
  onSuccess,
  categories = [],
  initialData = null,
}) => {
  const [form, setForm] = useState({
    roleName: "",
    category: "",
    description: "",
    image: "",
    location: "",
    salary: "",
    vacancies: "",
    experience: "",
    isActive: true,
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        roleName: initialData?.roleName || "",
        category: initialData?.category?._id || initialData?.category || "",
        description: initialData?.description || "",
        image: initialData?.image || "",
        location: initialData?.location || "",
        salary: initialData?.salary || "",
        vacancies: initialData?.vacancies || "",
        experience: initialData?.experience || "",
        isActive:
          typeof initialData?.isActive === "boolean"
            ? initialData.isActive
            : true,
      });
    }
  }, [open, initialData]);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.roleName.trim() || !form.category) {
      return toast.error("Role name and category are required");
    }

    try {
      setSubmitting(true);

      let res;
      if (mode === "create") {
        res = await axios.post(`${API}/job-post/create`, form);
      } else {
        res = await axios.put(
          `${API}/job-post/update/${initialData._id}`,
          form,
        );
      }

      if (res.data.success) {
        toast.success(
          mode === "create"
            ? "Job post created successfully"
            : "Job post updated successfully",
        );
        onSuccess?.();
        onClose();
      }
    } catch (error) {
      console.error("Job post submit error:", error);
      toast.error(error?.response?.data?.message || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalWrapper
      title={mode === "create" ? "Create Job Post" : "Edit Job Post"}
      onClose={onClose}
      maxWidth="max-w-3xl"
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Role Name
            </label>
            <input
              type="text"
              value={form.roleName}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, roleName: e.target.value }))
              }
              className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-primaryBtnColor"
              placeholder="Enter role name"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Category
            </label>
            <select
              value={form.category}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, category: e.target.value }))
              }
              className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-primaryBtnColor"
            >
              <option value="">Select category</option>
              {categories.map((cat) => (
                <option key={cat._id} value={cat._id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Location <span className="text-xs text-gray-400">(Optional)</span>
            </label>
            <input
              type="text"
              value={form.location}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, location: e.target.value }))
              }
              className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-primaryBtnColor"
              placeholder="e.g. Remote"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Salary <span className="text-xs text-gray-400">(Optional)</span>
            </label>
            <input
              type="text"
              value={form.salary}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, salary: e.target.value }))
              }
              className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-primaryBtnColor"
              placeholder="e.g. ₹8-12 LPA"
            />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Vacancies{" "}
              <span className="text-xs text-gray-400">(Optional)</span>
            </label>
            <input
              type="number"
              value={form.vacancies}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, vacancies: e.target.value }))
              }
              className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-primaryBtnColor"
              placeholder="e.g. 5"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Experience{" "}
              <span className="text-xs text-gray-400">(Optional)</span>
            </label>
            <input
              type="text"
              value={form.experience}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, experience: e.target.value }))
              }
              className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-primaryBtnColor"
              placeholder="e.g. 2-4 Years"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Description
          </label>
          <div className="rounded-xl overflow-hidden border">
            <ReactQuill
              theme="snow"
              value={form.description}
              onChange={(value) =>
                setForm((prev) => ({ ...prev, description: value }))
              }
              placeholder="Enter job description"
              className="bg-white"
            />
          </div>
        </div>

        <div className="flex items-center justify-between bg-gray-50 rounded-xl p-4">
          <span className="font-medium text-gray-700">Active Status</span>
          <ToggleSwitch
            checked={form.isActive}
            onChange={() =>
              setForm((prev) => ({ ...prev, isActive: !prev.isActive }))
            }
          />
        </div>

        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="bg-gray-100 text-primaryTextColor py-2.5 px-6 rounded-lg hover:bg-gray-200"
          >
            Cancel
          </button>

          <button
            type="submit"
            disabled={submitting}
            className="bg-primaryBtnColor text-white py-2.5 px-6 rounded-lg hover:bg-primaryBtnHoverColor disabled:opacity-70"
          >
            {submitting
              ? mode === "create"
                ? "Creating..."
                : "Updating..."
              : mode === "create"
                ? "Create Job Post"
                : "Update Job Post"}
          </button>
        </div>
      </form>
    </ModalWrapper>
  );
};

export default JobPostFormModal;
